import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarPlus, CheckCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CALENDAR_CATEGORIES } from '@shared/schema';

import { apiRequest, fetchJson } from '@/lib/queryClient';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const CATEGORY_LABELS: Record<string, string> = {
  community: 'Community',
  market: 'Market / Fair',
  arts: 'Arts & Culture',
  food: 'Food & Drink',
  fitness: 'Fitness & Wellness',
  music: 'Music',
  outdoors: 'Outdoors',
  education: 'Education',
  family: 'Family',
  other: 'Other',
};

const formSchema = z.object({
  website: z.string().max(0).optional(), // honeypot
  submitterName: z.string().min(1, 'Name is required').max(200),
  submitterEmail: z.string().email('Enter a valid email').max(320),
  submitterMessage: z.string().max(2000).optional(),
  consent: z.boolean().refine(v => v, 'You must agree to continue'),
  title: z.string().min(2, 'Event title is required').max(200),
  category: z.string().min(1, 'Select a category'),
  description: z.string().max(4000).optional(),
  startsAt: z.string().min(1, 'Start date/time is required'),
  endsAt: z.string().optional(),
  localityId: z.string().optional(),
  venueId: z.string().optional(),
  locationSuggestion: z.string().max(300).optional(),
  externalUrl: z.string().url('Enter a valid URL').max(500).optional().or(z.literal('')),
});

type FormValues = z.infer<typeof formSchema>;

export default function CalendarSubmit() {
  const [submitted, setSubmitted] = useState(false);

  const { data: localitiesData } = useQuery<{
    data: { id: string; name: string; slug: string; regionId: string | null }[];
  }>({
    queryKey: ['calendar-localities'],
    queryFn: () =>
      fetchJson<{ data: { id: string; name: string; slug: string; regionId: string | null }[] }>(
        '/api/calendar/localities'
      ),
  });

  const { data: venuesData } = useQuery<{
    data: { id: string; name: string; localityId: string | null }[];
  }>({
    queryKey: ['calendar-venues'],
    queryFn: () =>
      fetchJson<{ data: { id: string; name: string; localityId: string | null }[] }>(
        '/api/calendar/venues'
      ),
  });

  const localities = localitiesData?.data ?? [];
  const venues = venuesData?.data ?? [];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      website: '',
      category: 'community',
      consent: false,
    },
  });

  const selectedLocalityId = watch('localityId');
  const filteredVenues = selectedLocalityId
    ? venues.filter(v => v.localityId === selectedLocalityId)
    : venues;

  const submitMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const res = await apiRequest('POST', '/api/calendar/submit', {
        ...data,
        localityId: data.localityId || undefined,
        venueId: data.venueId || undefined,
        externalUrl: data.externalUrl || undefined,
        endsAt: data.endsAt || undefined,
      });
      return res.json();
    },
    onSuccess: () => setSubmitted(true),
  });

  if (submitted) {
    return (
      <PublicShell routeKey="calendar-submit">
        <section className="page-section">
          <div className="page-shell-narrow text-center space-y-6 py-16">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-semibold text-foreground">Submission received</h1>
            <p className="section-copy">
              Thanks for submitting your event. We'll review it and send you an email when we've
              made a decision. Approval is not guaranteed, but we read every submission.
            </p>
          </div>
        </section>
      </PublicShell>
    );
  }

  return (
    <PublicShell routeKey="calendar-submit">
      <section className="hero-gradient overflow-hidden border-b border-border/60">
        <div className="page-shell py-12">
          <div className="flex items-center gap-3 mb-4">
            <CalendarPlus className="w-6 h-6 text-primary" />
            <p className="editorial-kicker">Submit an Event</p>
          </div>
          <h1 className="section-title max-w-2xl">Add your event to the local calendar</h1>
          <p className="section-copy mt-4 max-w-xl">
            Know about something happening in the community? Submit it here. Our team reviews every
            submission before it goes live.
          </p>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell-narrow">
          <form
            onSubmit={handleSubmit(data => submitMutation.mutate(data))}
            noValidate
            className="space-y-8"
          >
            {/* Honeypot — hidden from real users */}
            <input
              {...register('website')}
              type="text"
              autoComplete="off"
              tabIndex={-1}
              className="hidden"
              aria-hidden="true"
            />

            {/* ── About you ── */}
            <Card className="rounded-[1.75rem] border-border/70">
              <CardContent className="pt-6 space-y-5">
                <h2 className="text-lg font-semibold text-foreground">About you</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="submitterName">Your name</Label>
                    <Input
                      id="submitterName"
                      {...register('submitterName')}
                      placeholder="Jane Smith"
                      autoComplete="name"
                    />
                    {errors.submitterName && (
                      <p className="text-xs text-destructive">{errors.submitterName.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="submitterEmail">Your email</Label>
                    <Input
                      id="submitterEmail"
                      {...register('submitterEmail')}
                      type="email"
                      placeholder="jane@example.com"
                      autoComplete="email"
                    />
                    {errors.submitterEmail && (
                      <p className="text-xs text-destructive">{errors.submitterEmail.message}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="submitterMessage">
                    Message to our team{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="submitterMessage"
                    {...register('submitterMessage')}
                    placeholder="Anything we should know about this event or how to verify it?"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* ── Event details ── */}
            <Card className="rounded-[1.75rem] border-border/70">
              <CardContent className="pt-6 space-y-5">
                <h2 className="text-lg font-semibold text-foreground">Event details</h2>

                <div className="space-y-1.5">
                  <Label htmlFor="title">Event title</Label>
                  <Input
                    id="title"
                    {...register('title')}
                    placeholder="Farmers Market at City Hall"
                  />
                  {errors.title && (
                    <p className="text-xs text-destructive">{errors.title.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="category">Category</Label>
                  <Select defaultValue="community" onValueChange={v => setValue('category', v)}>
                    <SelectTrigger id="category" className="rounded-xl">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CALENDAR_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {CATEGORY_LABELS[cat] ?? cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && (
                    <p className="text-xs text-destructive">{errors.category.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="description">
                    Description{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="Tell people what to expect…"
                    rows={4}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="startsAt">Starts</Label>
                    <Input id="startsAt" {...register('startsAt')} type="datetime-local" />
                    {errors.startsAt && (
                      <p className="text-xs text-destructive">{errors.startsAt.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="endsAt">
                      Ends <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input id="endsAt" {...register('endsAt')} type="datetime-local" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="externalUrl">
                    Event link <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="externalUrl"
                    {...register('externalUrl')}
                    type="url"
                    placeholder="https://example.com/event"
                  />
                  {errors.externalUrl && (
                    <p className="text-xs text-destructive">{errors.externalUrl.message}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ── Location ── */}
            <Card className="rounded-[1.75rem] border-border/70">
              <CardContent className="pt-6 space-y-5">
                <h2 className="text-lg font-semibold text-foreground">Location</h2>

                {localities.length > 0 && (
                  <div className="space-y-1.5">
                    <Label htmlFor="localityId">
                      Area / city{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Select onValueChange={v => setValue('localityId', v)}>
                      <SelectTrigger id="localityId" className="rounded-xl">
                        <SelectValue placeholder="Select an area" />
                      </SelectTrigger>
                      <SelectContent>
                        {localities.map(l => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {filteredVenues.length > 0 && (
                  <div className="space-y-1.5">
                    <Label htmlFor="venueId">
                      Venue <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Select onValueChange={v => setValue('venueId', v)}>
                      <SelectTrigger id="venueId" className="rounded-xl">
                        <SelectValue placeholder="Select a venue" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredVenues.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="locationSuggestion">
                    Location name or address{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="locationSuggestion"
                    {...register('locationSuggestion')}
                    placeholder="Ivy Tech Community College, Room 201"
                  />
                </div>
              </CardContent>
            </Card>

            {/* ── Consent + submit ── */}
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  onCheckedChange={checked => setValue('consent', !!checked)}
                />
                <label
                  htmlFor="consent"
                  className="text-sm text-muted-foreground leading-5 cursor-pointer"
                >
                  I confirm this event is real and happening in the local community. I understand
                  approval is not guaranteed and my email will only be used to follow up on this
                  submission.
                </label>
              </div>
              {errors.consent && (
                <p className="text-xs text-destructive">{errors.consent.message}</p>
              )}

              {submitMutation.error && (
                <p className="text-sm text-destructive">
                  {(submitMutation.error as Error).message}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                className="touch-target rounded-full px-8 font-semibold w-full sm:w-auto"
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit event'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </PublicShell>
  );
}
